import { Spinner } from "@swivl-mono/ui";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IoPause, IoPlay } from "react-icons/io5";

import { formatDuration } from "../../../../../Helpers/Time/formatDuration";
import { RecordingDownloadDropdownButton } from "../RecordingDownloadDropdownButton/RecordingDownloadDropdownButton";

interface RecordingPlayerProps {
  audioSrc?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
}

export const RecordingPlayer: React.FC<RecordingPlayerProps> = ({ audioSrc, onPlay, onPause, onEnded }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const pendingSeekTimeRef = useRef<number | null>(null);
  const previousPlaybackRateRef = useRef<number>(1);
  const [isDragging, setIsDragging] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState<number | null>(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [playbackRate, setPlaybackRate] = useState<number>(1);

  const playbackRates = [1, 1.2, 1.5, 2];

  const effectiveDuration = useMemo(() => {
    return duration && duration > 0 ? duration : 0;
  }, [duration]);

  useEffect(() => {
    if (!audioSrc) {
      setIsLoading(false);
      setCurrentTime(0);
      setDuration(null);
      setIsPlaying(false);
      return;
    }

    // Reset all states when audio source changes
    setIsLoading(true);
    setHasError(false);
    setCurrentTime(0);
    setDuration(null);
    setIsPlaying(false);
    pendingSeekTimeRef.current = null;

    // Use the same audio element, just change src
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    const audioEl = audioRef.current;

    // Only reload if the src actually changed
    const isNewSrc = audioEl.src !== audioSrc;
    if (isNewSrc) {
      audioEl.src = audioSrc;
      audioEl.load(); // Reload with new src
    }

    let isInitialLoad = true;

    const handleLoadedMetadata = () => {
      if (audioEl.duration && !Number.isNaN(audioEl.duration)) {
        setDuration(audioEl.duration);
      }
      setHasError(false);
      setIsLoading(false);
      audioEl.playbackRate = playbackRate;
      isInitialLoad = false;
      // Apply pending seek position after metadata is loaded
      if (pendingSeekTimeRef.current !== null && audioEl.readyState >= 1) {
        try {
          audioEl.currentTime = pendingSeekTimeRef.current;
          setCurrentTime(pendingSeekTimeRef.current);
          pendingSeekTimeRef.current = null;
        } catch (e) {
          // Ignore errors
        }
      }
    };

    const handleLoadStart = () => {
      // Only set loading to true if this is the initial load or if audio is not ready
      // Don't set loading during seek operations when audio is already partially loaded
      if (isInitialLoad || audioEl.readyState < 2) {
        setIsLoading(true);
      }
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      isInitialLoad = false;
      // Apply pending seek position if it was set before audio was ready
      if (pendingSeekTimeRef.current !== null) {
        try {
          audioEl.currentTime = pendingSeekTimeRef.current;
          setCurrentTime(pendingSeekTimeRef.current);
          pendingSeekTimeRef.current = null;
        } catch (e) {
          // Ignore errors
        }
      }
    };

    const handleLoadedData = () => {
      setIsLoading(false);
      isInitialLoad = false;
      // Apply pending seek position when data is loaded
      if (pendingSeekTimeRef.current !== null) {
        try {
          audioEl.currentTime = pendingSeekTimeRef.current;
          setCurrentTime(pendingSeekTimeRef.current);
          pendingSeekTimeRef.current = null;
        } catch (e) {
          // Ignore errors
        }
      }
    };

    const handleCanPlayThrough = () => {
      setIsLoading(false);
      isInitialLoad = false;
    };

    // Fallback check - reset loading after timeout if events don't fire
    const loadingTimeout = setTimeout(() => {
      if (audioEl.readyState >= 1) {
        setIsLoading(false);
        isInitialLoad = false;
      }
    }, 3000);

    const handleEnded = () => {
      setIsPlaying(false);
      audioEl.currentTime = 0;
      setCurrentTime(0);
      onEnded?.();
    };

    const handleError = () => {
      setHasError(true);
      setIsPlaying(false);
      setIsLoading(false);
      isInitialLoad = false;
    };

    audioEl.addEventListener("loadstart", handleLoadStart);
    audioEl.addEventListener("loadedmetadata", handleLoadedMetadata);
    audioEl.addEventListener("loadeddata", handleLoadedData);
    audioEl.addEventListener("canplay", handleCanPlay);
    audioEl.addEventListener("canplaythrough", handleCanPlayThrough);
    audioEl.addEventListener("ended", handleEnded);
    audioEl.addEventListener("error", handleError);

    return () => {
      // Stop playback when source changes
      clearTimeout(loadingTimeout);
      if (audioEl) {
        audioEl.pause();
        audioEl.removeEventListener("loadstart", handleLoadStart);
        audioEl.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audioEl.removeEventListener("loadeddata", handleLoadedData);
        audioEl.removeEventListener("canplay", handleCanPlay);
        audioEl.removeEventListener("canplaythrough", handleCanPlayThrough);
        audioEl.removeEventListener("ended", handleEnded);
        audioEl.removeEventListener("error", handleError);
      }
      setIsLoading(false);
      setIsPlaying(false);
      setCurrentTime(0);
      pendingSeekTimeRef.current = null;
    };
  }, [audioSrc, onEnded]);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }

    const audioEl = audioRef.current;
    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(audioEl.currentTime || 0);
      }
    };

    audioEl.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      audioEl.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [isDragging]);

  // Apply playbackRate when it changes, preserving current position and playback state
  useEffect(() => {
    // Skip if playbackRate hasn't actually changed (initial mount)
    if (previousPlaybackRateRef.current === playbackRate) {
      return;
    }

    if (!audioRef.current || !audioSrc) {
      previousPlaybackRateRef.current = playbackRate;
      return;
    }

    const audioEl = audioRef.current;

    // Save current state directly from audio element before changing playbackRate
    const wasPlaying = !audioEl.paused;
    const savedTime = audioEl.currentTime;

    // Set the new playback rate
    audioEl.playbackRate = playbackRate;
    previousPlaybackRateRef.current = playbackRate;

    // Restore the position immediately if audio is ready
    if (audioEl.readyState >= 1 && savedTime >= 0) {
      try {
        audioEl.currentTime = savedTime;
        setCurrentTime(savedTime);
      } catch (e) {
        // Ignore errors if setting currentTime fails
      }
    }

    // If it was playing, ensure it continues playing
    if (wasPlaying && audioEl.paused) {
      const playPromise = audioEl.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
            onPlay?.();
          })
          .catch(() => {
            setIsPlaying(false);
          });
      }
    } else if (wasPlaying && !audioEl.paused) {
      setIsPlaying(true);
    }
  }, [playbackRate]);

  const handleTogglePlay = async () => {
    if (!audioRef.current || !audioSrc || hasError || isLoading) {
      return;
    }

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        onPause?.();
      } else {
        let attempts = 0;
        const maxAttempts = 40;

        while (audioRef.current && audioRef.current.readyState < 2 && attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 50));
          attempts++;
        }

        if (audioRef.current) {
          const applySeek = () => {
            if (pendingSeekTimeRef.current !== null) {
              audioRef.current!.currentTime = pendingSeekTimeRef.current;
              setCurrentTime(pendingSeekTimeRef.current);
              pendingSeekTimeRef.current = null;
            } else if (currentTime > 0) {
              audioRef.current!.currentTime = currentTime;
            }
          };

          if (audioRef.current.readyState >= 2) {
            applySeek();
            await audioRef.current.play();
            setIsPlaying(true);
            onPlay?.();
          } else {
            const onReady = () => {
              applySeek();
              audioRef.current?.play().then(() => {
                setIsPlaying(true);
                onPlay?.();
              });
              audioRef.current?.removeEventListener("canplay", onReady);
            };
            audioRef.current.addEventListener("canplay", onReady, { once: true });
          }
        }
      }
    } catch {
      setHasError(true);
      setIsPlaying(false);
    }
  };

  const updateSeekPosition = useCallback(
    (clientX: number) => {
      if (!audioRef.current || !timelineRef.current || !audioSrc || hasError || !effectiveDuration) {
        return;
      }

      if (isLoading && (!audioRef.current.duration || audioRef.current.duration === 0)) {
        return;
      }

      const rect = timelineRef.current.getBoundingClientRect();
      const offsetX = clientX - rect.left;
      const ratio = Math.min(Math.max(offsetX / rect.width, 0), 1);
      const newTime = ratio * effectiveDuration;

      // Set position if we have at least metadata (readyState >= 1)
      // HAVE_METADATA means we can safely set currentTime
      if (audioRef.current.readyState >= 1) {
        try {
          audioRef.current.currentTime = newTime;
          setCurrentTime(newTime);
          pendingSeekTimeRef.current = null;
        } catch (e) {
          // If setting failed, save for later application
          pendingSeekTimeRef.current = newTime;
          setCurrentTime(newTime);
        }
      } else {
        // If audio is not ready yet, save desired position for later application
        pendingSeekTimeRef.current = newTime;
        setCurrentTime(newTime);
      }
    },
    [audioSrc, hasError, effectiveDuration, isLoading]
  );

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      return;
    }
    updateSeekPosition(event.clientX);
  };

  const handleDragStart = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !audioSrc || hasError || !effectiveDuration) {
      return;
    }

    // Allow dragging even during initial loading if metadata is available
    if (isLoading && (!audioRef.current.duration || audioRef.current.duration === 0)) {
      return;
    }

    event.preventDefault();
    setIsDragging(true);
    updateSeekPosition(event.clientX);
  };

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      updateSeekPosition(event.clientX);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, updateSeekPosition]);

  const progressRatio = useMemo(() => {
    if (!effectiveDuration || effectiveDuration <= 0) {
      return 0;
    }

    return Math.min(Math.max(currentTime / effectiveDuration, 0), 1);
  }, [currentTime, effectiveDuration]);

  const isDisabled = !audioSrc || hasError || isLoading;

  const currentLabel = effectiveDuration ? formatDuration(currentTime) : "--:--";
  const totalLabel = effectiveDuration ? formatDuration(effectiveDuration) : "--:--";

  const handleSpeedToggle = () => {
    if (isDisabled) {
      return;
    }

    const currentIndex = playbackRates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % playbackRates.length;
    setPlaybackRate(playbackRates[nextIndex]);
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={handleTogglePlay}
        className={`group relative flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
          isDisabled ? "cursor-not-allowed bg-gray-100" : "cursor-pointer bg-orange-50 hover:bg-orange-100"
        }`}
        disabled={isDisabled}
        aria-label={isPlaying ? "Pause recording" : "Play recording"}
      >
        {isLoading ? (
          <Spinner />
        ) : isPlaying ? (
          <IoPause className={`h-5 w-5 transition-colors ${isDisabled ? "text-gray-400" : "text-orange-500"}`} />
        ) : (
          <IoPlay className={`h-4 w-4 transition-colors ${isDisabled ? "text-gray-400" : "text-orange-500"}`} />
        )}
      </button>

      <button
        onClick={handleSpeedToggle}
        disabled={isDisabled}
        className={`w-8 rounded-full border px-1 text-xs font-semibold transition-colors ${
          isDisabled
            ? "cursor-not-allowed border-gray-300 bg-white text-gray-400"
            : "cursor-pointer border-gray-900 bg-white text-gray-900 hover:bg-gray-200"
        }`}
        aria-label="Change playback speed"
      >
        {playbackRate}x
      </button>

      <div
        ref={timelineRef}
        className={`relative mx-1 h-1 flex-1 cursor-pointer rounded-[2px] bg-gray-100 shadow-[0_1px_2px_0_rgba(0,0,0,0.25)_inset] ${
          isDisabled ? "cursor-not-allowed opacity-60" : ""
        }`}
        onClick={handleSeek}
      >
        <div className="absolute inset-y-0 left-0 rounded-full bg-orange-500" style={{ width: `${progressRatio * 100}%` }} />
        <div
          className={`absolute top-1/2 h-3 w-3 translate-x-[-50%] -translate-y-1/2 rounded-full border-4 border-orange-500 bg-white ${
            isDragging ? "cursor-grabbing" : isDisabled ? "" : "cursor-grab"
          }`}
          style={{ left: `${progressRatio * 100}%` }}
          onMouseDown={handleDragStart}
        />
      </div>

      <div className="heading-mono whitespace-nowrap text-grey-600">
        {currentLabel}/{totalLabel}
      </div>

      <RecordingDownloadDropdownButton audioSrc={audioSrc} isDisabled={isDisabled} />

      {hasError && (
        <div className="ml-2 text-xs text-red-600" title="Failed to load audio">
          Failed to load
        </div>
      )}
    </div>
  );
};
